import type { TeamTag } from '@rhythmic/db/types'
import type { Transaction } from '@tanstack/vue-db'
import { z } from 'zod'
import { useRhythmicLogger } from '#imports'

import { getTeamTagsCollection } from '../../getCollections'

const logger = useRhythmicLogger('useTeamContext:updateTeamTag')

export interface UpdateTeamTagPayload {
  tag?: string
  color?: string | null
  type?: string | null
  context?: string | null
}

export interface UpdateTeamTagContext {
  teamId: string
}

const UpdateTeamTagSchema = z.object({
  tag: z.string().min(1).optional(),
  color: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  context: z.string().nullable().optional(),
})

function validateParams(id: string | undefined, payload: UpdateTeamTagPayload, context: UpdateTeamTagContext) {
  if (!id) throw new Error('Tag identifier is required')

  if (!context.teamId) throw new Error('Team context is not ready')

  const parsed = UpdateTeamTagSchema.parse(payload)

  const changes = Object.fromEntries(
    Object.entries(parsed).filter(([, value]) => value !== undefined),
  ) as UpdateTeamTagPayload

  if (Object.keys(changes).length === 0) throw new Error('No changes provided to update team tag')

  return {
    id,
    teamId: context.teamId,
    changes,
  }
}

export default function handleUpdateTeamTag(
  id: string,
  payload: UpdateTeamTagPayload,
  context: UpdateTeamTagContext,
): Transaction<TeamTag> {
  try {
    const { teamId, changes } = validateParams(id, payload, context)
    const teamTagsCollection = getTeamTagsCollection(teamId)

    const tx = teamTagsCollection.update(id, (draft: TeamTag) => {
      if (changes.tag !== undefined) draft.tag = changes.tag
      if (changes.color !== undefined) draft.color = changes.color
      if (changes.type !== undefined) draft.type = changes.type
      if (changes.context !== undefined) draft.context = changes.context
      draft.updated_at = new Date()
    })

    return tx as Transaction<TeamTag>
  } catch (error) {
    logger.error('updateTeamTag failed', { payload, error })
    if (error instanceof Error) throw error
    throw new Error('Failed to update team tag')
  }
}
